import { QueryClient } from '@tanstack/react-query';

import { SidecarRoute } from '$components/sidecar-router/sidecar-router.definitions';
import { AuditEventDetailWrap } from '$features/audit-events/detail/audit-event-detail-wrap';
import { AssociatedBox } from '$features/devices/associated-devices/associated-box/associated-box';
import { AssociatedDevices } from '$features/devices/associated-devices/associated-devices/associated-devices';
import { AssociatedMonitors } from '$features/devices/associated-devices/associated-monitors/associated-monitors';
import { AssociatedRouter } from '$features/devices/associated-devices/associated-router/associated-router';
import { DeviceInformation } from '$features/devices/device-information/device-information';
import { DeviceNotes } from '$features/devices/device-information/device-notes';
import { IncidentFlow } from '$features/incidents/incident-flow';
import { MonitoringEvents } from '$features/monitoring/events/monitoring-events';
import { EmailMonitoringExport } from '$features/monitoring/export/email-monitoring-export';
import { HistoryChartsV2 } from '$features/monitoring/history-charts-v2/history-charts-v2';
import { MonitoringParameters } from '$features/monitoring/parameters/monitoring-parameters';
import { RunRecordDetailWrap } from '$features/report-hub/run-record/run-record-detail-wrap';
import { StabilityReportDetailWrap } from '$features/report-hub/stability-report/stability-report-detail-wrap';
import { UserPreferences } from '$features/show-user/user-preferences';
import { UserProfile } from '$features/show-user/user-profile';
import {
  auditDetailLoader,
  AuditEventDetailPage,
} from '$pages/audit-events/detail/audit-events-detail-page';
import {
  DeviceDetailPage,
  deviceDetailLoader,
} from '$pages/devices/detail/device-detail-page';
import {
  IncidentDetailPage,
  incidentDetailLoader,
} from '$pages/incidents/detail/incident-detail-page';
import { MonitoringDetailPage } from '$pages/monitoring/detail/monitoring-detail-page';
import {
  NotificationRuleDetailPage,
  notificationRuleDetailLoader,
} from '$pages/notifications/detail/notification-detail-page';
import {
  runRecordDetailLoader,
  RunRecordDetailPage,
} from '$pages/report-hub/detail/run-record-detail-page';
import {
  stabilityReportDetailLoader,
  StabilityReportDetailPage,
} from '$pages/report-hub/detail/stability-report-detail-page';
import {
  UserDetailPage,
  userDetailLoader,
} from '$pages/user-management/detail/user-detail-page';

enum SideCarRoutes {
  Device = 'device',
  Monitoring = 'monitoring',
  Notification = 'notifications',
  User = 'user',
  Incident = 'incident',
  AuditLog = 'audit-log',
  StabilityReport = 'stability-report',
  RunRecord = 'run-record',
}

export function createSidecarRoutes(
  queryClient: QueryClient,
): Record<SideCarRoutes, SidecarRoute> {
  return {
    [SideCarRoutes.Device]: {
      element: <DeviceDetailPage />,
      loader: async ({ params }) => {
        if (
          !params.get('manufacturer') ||
          params.get('manufacturer') === null ||
          !params.get('serialNumber') ||
          params.get('serialNumber') === null
        ) {
          return Promise.reject(new Error('Invalid params'));
        }

        return deviceDetailLoader(queryClient, {
          manufacturer: params.get('manufacturer')!,
          serialNumber: params.get('serialNumber')!,
        });
      },
      children: {
        details: {
          element: <DeviceInformation />,
        },
        notes: {
          element: <DeviceNotes />,
        },
        associatedRouter: {
          element: <AssociatedRouter />,
        },
        associatedMonitors: {
          element: <AssociatedMonitors />,
        },
        associatedDevices: {
          element: <AssociatedDevices />,
        },
        associatedBox: {
          element: <AssociatedBox />,
        },
      },
    },
    [SideCarRoutes.Monitoring]: {
      element: <MonitoringDetailPage />,
      children: {
        parameters: {
          element: <MonitoringParameters />,
        },
        events: {
          element: <MonitoringEvents />,
        },
        export: {
          element: <EmailMonitoringExport />,
        },
        'history-charts-v2': {
          element: <HistoryChartsV2 />,
        },
      },
    },
    [SideCarRoutes.Notification]: {
      element: <NotificationRuleDetailPage />,
      loader: async ({ params }) => {
        if (!params.get('id') || params.get('id') === null) {
          return Promise.reject(new Error('Invalid params'));
        }

        return notificationRuleDetailLoader(queryClient, params.get('id')!);
      },
    },
    [SideCarRoutes.User]: {
      element: <UserDetailPage />,
      loader: async ({ params }) => {
        if (!params.get('id') || params.get('id') === null) {
          return Promise.reject(new Error('Invalid params'));
        }

        return userDetailLoader(queryClient, params.get('id')!);
      },
      children: {
        profile: {
          element: <UserProfile />,
        },
        preferences: {
          element: <UserPreferences />,
        },
      },
    },
    [SideCarRoutes.Incident]: {
      element: <IncidentDetailPage />,
      loader: async ({ params }) => {
        const incidentId = params.get('id');

        if (!incidentId) {
          return Promise.reject(new Error('Invalid params'));
        }

        return incidentDetailLoader(queryClient, incidentId);
      },
      children: {
        flow: {
          element: <IncidentFlow />,
        },
      },
    },
    [SideCarRoutes.AuditLog]: {
      element: <AuditEventDetailPage />,
      loader: async ({ params }) => {
        const auditEventId = params.get('id');

        if (!auditEventId) {
          return Promise.reject(new Error('Invalid params'));
        }

        return auditDetailLoader(queryClient, auditEventId);
      },
      children: {
        detail: {
          element: <AuditEventDetailWrap />,
        },
      },
    },
    [SideCarRoutes.StabilityReport]: {
      element: <StabilityReportDetailPage />,
      loader: async ({ params }) => {
        const reportId = params.get('id');

        if (!reportId) {
          return Promise.reject(new Error('Invalid params'));
        }

        return stabilityReportDetailLoader(queryClient, reportId);
      },
      children: {
        detail: {
          element: <StabilityReportDetailWrap />,
        },
      },
    },
    [SideCarRoutes.RunRecord]: {
      element: <RunRecordDetailPage />,
      loader: async ({ params }) => {
        const reportId = params.get('id');

        if (!reportId) {
          return Promise.reject(new Error('Invalid params'));
        }

        return runRecordDetailLoader(queryClient, reportId);
      },
      children: {
        detail: {
          element: <RunRecordDetailWrap />,
        },
      },
    },
  };
}
